import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  X, 
  ShoppingCart, 
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ArrowRight,
  Sparkles,
  GripVertical,
  Lock,
  Check,
  Utensils,
  Clock,
  Heart,
  Wand2,
  Share2,
  Users,
  RefreshCw,
  Wifi,
  WifiOff,
  Bell
} from 'lucide-react';
import { format, startOfWeek, addDays, isSameDay, addWeeks, subWeeks, isToday } from 'date-fns';
import axios from 'axios';
import ShareMealPlanDialog from '@/components/ShareMealPlanDialog';
import MobileMealPlanner from '@/components/MobileMealPlanner';
import { 
  SwapMealSheet, 
  QuickEditBar, 
  DietaryFilterSheet,
  ComponentSwapSheet 
} from '@/components/LightEdit';
import { 
  trackDemoStepViewed, 
  trackDemoStepCompleted, 
  trackMealAdded, 
  trackMealRemoved,
  trackAutoPlanUsed,
  trackGroceryListGenerated,
  trackCartBuilt,
  trackMealSwapped
} from '@/utils/analytics';
import { haptic } from '@/utils/haptics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DINNER_GOAL = 5; // Target number of dinners to plan

// Dietary options
const DIETARY_OPTIONS = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'gluten-free', label: 'Gluten Free' },
  { value: 'dairy-free', label: 'Dairy Free' },
  { value: 'keto', label: 'Keto' },
  { value: 'paleo', label: 'Paleo' },
];

// Cuisine options
const CUISINE_OPTIONS = [
  { value: 'italian', label: 'Italian' },
  { value: 'mexican', label: 'Mexican' },
  { value: 'asian', label: 'Asian' },
  { value: 'american', label: 'American' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'indian', label: 'Indian' },
];

const MealPlanner = () => {
  const { api, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDemoMode = searchParams.get('demo') === 'true';
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [mealPlan, setMealPlan] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [pantryAlerts, setPantryAlerts] = useState([]);
  const [addMealMode, setAddMealMode] = useState('choose'); // 'choose', 'swap', 'browse', 'favorites'  
  // Auto-plan state
  const [autoPlanOpen, setAutoPlanOpen] = useState(false);
  const [autoPlanLoading, setAutoPlanLoading] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [autoPlanPrefs, setAutoPlanPrefs] = useState({
    numMeals: 5,
    dietary: [],
    cuisines: [],
    maxCookTime: null,
    useFavoritesFirst: true,
    fillMode: 'empty_only'
  });
  
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  
  // Household collaboration state
  const [inHousehold, setInHousehold] = useState(false);
  const [householdId, setHouseholdId] = useState('');
  const [householdName, setHouseholdName] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [activeMembersOnline, setActiveMembersOnline] = useState(0);
  const wsRef = useRef(null);
  
  // Demo mode state
  const [showFirstInteractionTooltip, setShowFirstInteractionTooltip] = useState(isDemoMode);
  const [demoMealsAdded, setDemoMealsAdded] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showStep3Banner, setShowStep3Banner] = useState(false);
  
  // Light Edit state
  const [servings, setServings] = useState(4);
  const [swapMealOpen, setSwapMealOpen] = useState(false);
  const [swapMealDayIndex, setSwapMealDayIndex] = useState(null);
  const [swapMealTitle, setSwapMealTitle] = useState('');
  const [dietaryFilterOpen, setDietaryFilterOpen] = useState(false);
  
  // Component swap state
  const [componentSwapOpen, setComponentSwapOpen] = useState(false);
  const [componentSwapMealId, setComponentSwapMealId] = useState(null);
  const [componentSwapMealTitle, setComponentSwapMealTitle] = useState('');
  const [componentSwapDayIndex, setComponentSwapDayIndex] = useState(null);
  
  // Function to open component swap sheet
  const openComponentSwap = (mealId, mealTitle, dayIndex) => {
    setComponentSwapMealId(mealId);
    setComponentSwapMealTitle(mealTitle);
    setComponentSwapDayIndex(dayIndex);
    setComponentSwapOpen(true);
    haptic('light');
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Load pantry alerts for mobile suggestions
  useEffect(() => {
    const loadPantryAlerts = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await api.get('/pantry');
        const now = new Date();
        const expiring = (response.data || []).filter(item => {
          if (!item.expires_at) return false;
          const expDate = new Date(item.expires_at);
          const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
          return daysLeft <= 3 && daysLeft >= 0;
        }).map(item => ({
          ...item,
          daysLeft: Math.ceil((new Date(item.expires_at) - now) / (1000 * 60 * 60 * 24))
        })).slice(0, 3);
        setPantryAlerts(expiring);
      } catch (error) {
        console.error('Failed to load pantry:', error);
      }
    };
    loadPantryAlerts();
  }, [api, isAuthenticated]);

  // Track demo step 2 viewed
  useEffect(() => {
    if (isDemoMode) {
      trackDemoStepViewed(2, 'Plan Your Week');
    }
  }, [isDemoMode]);

  // Mock recipes for demo mode
  const mockDemoRecipes = [
    { id: 716429, title: 'Pasta with Garlic & Breadcrumbs', image: 'https://img.spoonacular.com/recipes/716429-312x231.jpg', readyInMinutes: 45 },
    { id: 715538, title: 'Bruschetta Style Pork & Pasta', image: 'https://img.spoonacular.com/recipes/715538-312x231.jpg', readyInMinutes: 35 },
    { id: 663559, title: 'Tomato and Basil Chicken Pasta', image: 'https://img.spoonacular.com/recipes/663559-312x231.jpg', readyInMinutes: 30 },
    { id: 654959, title: 'One-Pot Lemon Chicken Pasta', image: 'https://img.spoonacular.com/recipes/654959-312x231.jpg', readyInMinutes: 25 },
  ];

  // Demo week auto-population - pre-fill with example meals so users never see an empty week
  const demoWeekMeals = [
    { id: 'd1', recipe_id: 'd1', recipe_title: 'Honey Garlic Chicken', title: 'Honey Garlic Chicken', recipe_image: 'https://img.spoonacular.com/recipes/654959-312x231.jpg', image: 'https://img.spoonacular.com/recipes/654959-312x231.jpg', readyInMinutes: 30 },
    { id: 'd2', recipe_id: 'd2', recipe_title: 'Sheet Pan Sausage & Veggies', title: 'Sheet Pan Sausage & Veggies', recipe_image: 'https://img.spoonacular.com/recipes/715538-312x231.jpg', image: 'https://img.spoonacular.com/recipes/715538-312x231.jpg', readyInMinutes: 35 },
    { id: 'd3', recipe_id: 'd3', recipe_title: 'Taco Tuesday', title: 'Taco Tuesday', recipe_image: 'https://img.spoonacular.com/recipes/663559-312x231.jpg', image: 'https://img.spoonacular.com/recipes/663559-312x231.jpg', readyInMinutes: 25 },
    { id: 'd4', recipe_id: 'd4', recipe_title: 'Baked Ziti', title: 'Baked Ziti', recipe_image: 'https://img.spoonacular.com/recipes/716429-312x231.jpg', image: 'https://img.spoonacular.com/recipes/716429-312x231.jpg', readyInMinutes: 40 },
    { id: 'd5', recipe_id: 'd5', recipe_title: 'Veggie Stir Fry', title: 'Veggie Stir Fry', recipe_image: 'https://img.spoonacular.com/recipes/654959-312x231.jpg', image: 'https://img.spoonacular.com/recipes/654959-312x231.jpg', readyInMinutes: 20 },
  ];

  // Auto-populate demo week when in demo mode
  useEffect(() => {
    if (isDemoMode && !isAuthenticated && Object.keys(mealPlan).length === 0 && !loading) {
      // Create a pre-filled week for demo users
      const demoWeek = {};
      const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      
      weekDays.forEach((day, index) => {
        const dateKey = format(day, 'yyyy-MM-dd');
        // Only add meals for weekdays (Mon-Fri)
        if (index >= 1 && index <= 5 && demoWeekMeals[index - 1]) {
          demoWeek[dateKey] = [demoWeekMeals[index - 1]];
        } else {
          demoWeek[dateKey] = [];
        }
      });
      
      setMealPlan(demoWeek);
      setDemoMealsAdded(5);
    }
  }, [isDemoMode, isAuthenticated, mealPlan, loading, weekStart]);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await api.get('/subscription/status');
        setIsPro(response.data.is_pro);
      } catch (error) {
        console.error('Failed to check subscription:', error);
      }
    };
    checkSubscription();
  }, [api, isAuthenticated]);

  // Check household membership
  useEffect(() => {
    const checkHousehold = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await api.get('/household');
        if (response.data.household) {
          setInHousehold(true);
          setHouseholdId(response.data.household.id);
          setHouseholdName(response.data.household.name);
        } else {
          setInHousehold(false);
          setHouseholdId('');
          setHouseholdName('');
        }
      } catch (error) {
        console.error('Failed to check household:', error);
      }
    };
    checkHousehold();
  }, [api, isAuthenticated]);

  const loadMealPlan = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const [planRes, favRes] = await Promise.all([
        api.get('/meal-plans'),
        api.get('/favorites')
      ]);

      if (planRes.data) {
        const planMap = {};
        planRes.data.days.forEach(day => {
          planMap[day.date] = day.meals;
        });
        setMealPlan(planMap);
        // Load servings from plan if available
        if (planRes.data.servings) {
          setServings(planRes.data.servings);
        }
      }

      setFavorites(favRes.data || []);
    } catch (error) {
      console.error('Failed to load meal plan:', error);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    loadMealPlan();
  }, [loadMealPlan]);

  // WebSocket connection for real-time household sync
  useEffect(() => {
    if (!isAuthenticated || !inHousehold || !householdId) return;
    
    // Get token from localStorage for WebSocket auth
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Create WebSocket URL (convert http(s) to ws(s))
    // Handle both localhost and production URLs
    let wsUrl;
    if (API_URL && API_URL.length > 0) {
      const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
      const wsHost = API_URL.replace(/^https?:\/\//, '');
      wsUrl = `${wsProtocol}://${wsHost}/ws/household/${householdId}?token=${token}`;
    } else {
      // Fallback for local development
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const host = window.location.hostname;
      wsUrl = `${protocol}://${host}:8001/ws/household/${householdId}?token=${token}`;
    }
    
    console.log('WebSocket connecting to:', wsUrl.substring(0, 50) + '...');
    
    let ws = null;
    let reconnectTimeout = null;
    let pingInterval = null;
    
    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected to household');
        setWsConnected(true);
        setLastSync(new Date());
        
        // Start ping interval to keep connection alive
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connected':
              setActiveMembersOnline(data.active_members);
              break;
              
            case 'meal_plan_updated':
              // Another household member updated the meal plan
              if (data.days) {
                const planMap = {};
                data.days.forEach(day => {
                  planMap[day.date] = day.meals;
                });
                setMealPlan(planMap);
                setLastSync(new Date());
                
                // Show notification
                toast.success(`${data.user_name} updated the meal plan`, {
                  icon: <Bell className="h-4 w-4" />,
                  duration: 3000
                });
              }
              break;
              
            case 'member_disconnected':
              setActiveMembersOnline(data.active_members);
              break;
              
            case 'pong':
              // Keepalive response, no action needed
              break;
              
            default:
              console.log('Unknown WebSocket message:', data);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setWsConnected(false);
        
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
        
        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000) {
          reconnectTimeout = setTimeout(connect, 5000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };
    
    connect();
    
    // Cleanup on unmount
    return () => {
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) {
        ws.close(1000, 'Component unmounting');
      }
    };
  }, [isAuthenticated, inHousehold, householdId]);

  // Fallback polling when WebSocket is disconnected (every 30 seconds)
  useEffect(() => {
    if (!isAuthenticated || !inHousehold || wsConnected) return;
    
    const pollMealPlan = async () => {
      try {
        const planRes = await api.get('/meal-plans');
        if (planRes.data) {
          const planMap = {};
          planRes.data.days.forEach(day => {
            planMap[day.date] = day.meals;
          });
          setMealPlan(planMap);
          setLastSync(new Date());
        }
      } catch (error) {
        console.error('Failed to sync meal plan:', error);
      }
    };
    
    // Poll every 30 seconds as fallback when WebSocket is disconnected
    const interval = setInterval(pollMealPlan, 30000);
    
    return () => clearInterval(interval);
  }, [api, isAuthenticated, inHousehold, wsConnected]);

  // Count meals for progress indicator
  useEffect(() => {
    const totalMeals = Object.values(mealPlan).reduce((acc, meals) => acc + meals.length, 0);
    setDemoMealsAdded(totalMeals);
    
    // Trigger Step 3 banner when at least 1 meal is added (demo mode only)
    if (isDemoMode && totalMeals >= 1) {
      setShowStep3Banner(true);
    }
  }, [mealPlan, isDemoMode]);

  const saveMealPlan = async (newPlan) => {
    // In demo mode, show signup modal instead of saving
    if (isDemoMode && !isAuthenticated) {
      setMealPlan(newPlan);
      return;
    }
    
    setSaving(true);
    try {
      const days = Object.entries(newPlan).map(([date, meals]) => ({
        date,
        meals
      }));

      console.log('Saving meal plan:', { days });
      await api.post('/meal-plans', { days });
      setMealPlan(newPlan);
      toast.success('Meal plan saved');
    } catch (error) {
      console.error('Save meal plan error:', error.response?.status, error.response?.data, error.message);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Upgrade to Pro for unlimited meal planning', {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/subscription')
          }
        });
      } else {
        toast.error('Failed to save meal plan');
      }
    } finally {
      setSaving(false);
    }
  };

  const addMealToDate = async (recipe) => {
    if (!selectedDate) return;

    // Hide first interaction tooltip after first add
    setShowFirstInteractionTooltip(false);

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const dayName = format(selectedDate, 'EEEE');
    const newMeal = {
      recipe_id: recipe.id || recipe.recipe_id,
      recipe_title: recipe.title || recipe.recipe_title,
      recipe_image: recipe.image || recipe.recipe_image,
      servings: 1
    };

    // Track meal added
    trackMealAdded(newMeal.recipe_title, dayName, isDemoMode);

    const newPlan = {
      ...mealPlan,
      [dateStr]: [...(mealPlan[dateStr] || []), newMeal]
    };

    await saveMealPlan(newPlan);
    setDialogOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    
    // Show encouragement in demo mode
    if (isDemoMode && !isAuthenticated) {
      const totalMeals = Object.values(newPlan).reduce((acc, meals) => acc + meals.length, 0);
      if (totalMeals === 1) {
        toast.success('Nice start! Add a few more meals to complete your week.');
        trackDemoStepCompleted(2, 'Plan Your Week', { meals_added: 1 });
      } else if (totalMeals >= 3) {
        toast.success('Looking great! Your grocery list is almost ready.');
      }
    }
  };

  const removeMealFromDate = async (dateStr, mealIndex) => {
    const meals = [...(mealPlan[dateStr] || [])];
    const removedMeal = meals[mealIndex];
    meals.splice(mealIndex, 1);

    // Track meal removed
    if (removedMeal) {
      trackMealRemoved(removedMeal.recipe_title, isDemoMode);
    }

    const newPlan = {
      ...mealPlan,
      [dateStr]: meals
    };

    if (meals.length === 0) {
      delete newPlan[dateStr];
    }

    await saveMealPlan(newPlan);
  };

  const searchRecipes = async () => {
    if (!searchQuery.trim()) return;

    setSearchLoading(true);
    try {
      const response = isAuthenticated 
        ? await api.post('/recipes/search', { query: searchQuery, number: 6 })
        : await axios.post(`${API_URL}/api/recipes/search`, { query: searchQuery, number: 6 });
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast.error('Failed to search recipes');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAutoPlan = async () => {
    if (!isPro) {
      toast.error('Auto-plan is a Pro feature. Upgrade to unlock!', {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/subscription')
        }
      });
      return;
    }
    
    setAutoPlanLoading(true);
    try {
      const response = await api.post('/meal-plans/auto-generate', {
        num_meals: autoPlanPrefs.numMeals,
        dietary: autoPlanPrefs.dietary.length > 0 ? autoPlanPrefs.dietary : null,
        cuisines: autoPlanPrefs.cuisines.length > 0 ? autoPlanPrefs.cuisines : null,
        max_cook_time: autoPlanPrefs.maxCookTime,
        use_favorites_first: autoPlanPrefs.useFavoritesFirst,
        fill_mode: autoPlanPrefs.fillMode
      });
      
      // Update the meal plan state
      const planMap = {};
      response.data.days.forEach(day => {
        planMap[day.date] = day.meals;
      });
      setMealPlan(planMap);
      
      setAutoPlanOpen(false);
      toast.success('Your week has been planned! 🎉', {
        description: `${autoPlanPrefs.numMeals} meals added to your calendar`
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate meal plan';
      toast.error(message);
    } finally {
      setAutoPlanLoading(false);
    }
  };

  const toggleDietary = (value) => {
    setAutoPlanPrefs(prev => ({
      ...prev,
      dietary: prev.dietary.includes(value)
        ? prev.dietary.filter(d => d !== value)
        : [...prev.dietary, value]
    }));
  };

  const toggleCuisine = (value) => {
    setAutoPlanPrefs(prev => ({
      ...prev,
      cuisines: prev.cuisines.includes(value)
        ? prev.cuisines.filter(c => c !== value)
        : [...prev.cuisines, value]
    }));
  };

  const generateGroceryList = async () => {
    // In demo mode, navigate to grocery lists page to continue demo
    if (isDemoMode && !isAuthenticated) {
      const hasRecipes = Object.values(mealPlan).some(meals => meals.length > 0);
      if (!hasRecipes) {
        toast.error('Add some recipes to your meal plan first');
        return;
      }
      const totalItems = Object.values(mealPlan).reduce((acc, meals) => acc + meals.length, 0);
      trackGroceryListGenerated(totalItems, true);
      trackDemoStepCompleted(3, 'Generate Grocery List', { item_count: totalItems });
      toast.success('Grocery list generated!');
      navigate('/grocery-lists?demo=true&generated=true');
      return;
    }
    
    const hasRecipes = Object.values(mealPlan).some(meals => meals.length > 0);
    if (!hasRecipes) {
      toast.error('Add some recipes to your meal plan first');
      return;
    }

    try {
      await api.post('/grocery-lists/from-meal-plan');
      const totalItems = Object.values(mealPlan).reduce((acc, meals) => acc + meals.length, 0);
      trackGroceryListGenerated(totalItems, false);
      // Track cart built for funnel analytics (estimate ~$12 per meal)
      trackCartBuilt(totalItems, totalItems * 12);
      toast.success('Grocery list generated!');
      navigate('/grocery-lists');
    } catch (error) {
      toast.error('Failed to generate grocery list');
    }
  };

  const clearWeek = async () => {
    const weekDates = weekDays.map(d => format(d, 'yyyy-MM-dd'));
    const newPlan = { ...mealPlan };
    weekDates.forEach(date => delete newPlan[date]);
    await saveMealPlan(newPlan);
    if (!isDemoMode || isAuthenticated) {
      toast.success('Week cleared');
    }
  };

  const handleDemoSaveAttempt = () => {
    if (isDemoMode && !isAuthenticated) {
      setShowSignupModal(true);
    }
  };

  // Check if calendar is empty
  const isCalendarEmpty = Object.values(mealPlan).every(meals => meals.length === 0);

  // Mobile meal planner handlers
  const handleMobileAddMeal = async (day, recipe) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const dayName = format(day, 'EEEE');
    const newMeal = {
      recipe_id: recipe.id || recipe.recipe_id,
      recipe_title: recipe.title || recipe.recipe_title,
      recipe_image: recipe.image || recipe.recipe_image,
      servings: 1
    };

    trackMealAdded(newMeal.recipe_title, dayName, isDemoMode);
    haptic('success');

    const newPlan = {
      ...mealPlan,
      [dateStr]: [...(mealPlan[dateStr] || []), newMeal]
    };

    await saveMealPlan(newPlan);
  };

  const handleMobileRemoveMeal = async (dateStr, mealIndex) => {
    const meals = [...(mealPlan[dateStr] || [])];
    const removedMeal = meals[mealIndex];
    meals.splice(mealIndex, 1);

    if (removedMeal) {
      trackMealRemoved(removedMeal.recipe_title, isDemoMode);
    }
    haptic('light');

    const newPlan = {
      ...mealPlan,
      [dateStr]: meals
    };

    if (meals.length === 0) {
      delete newPlan[dateStr];
    }

    await saveMealPlan(newPlan);
  };

  const handleMobileAutoPlan = async () => {
    haptic('medium');
    setAutoPlanLoading(true);
    try {
      const response = await api.post('/meal-plans/auto-plan', {
        num_meals: 5,
        dietary: [],
        cuisines: [],
        max_cook_time: null,
        use_favorites_first: true,
        fill_mode: 'empty_only'
      });
      
      if (response.data?.days) {
        const planMap = {};
        response.data.days.forEach(day => {
          planMap[day.date] = day.meals;
        });
        setMealPlan(planMap);
        trackAutoPlanUsed({ numMeals: 5 });
      }
    } catch (error) {
      console.error('Auto-plan failed:', error);
    } finally {
      setAutoPlanLoading(false);
    }
  };

  const handleMobileGenerateList = async () => {
    haptic('medium');
    const hasRecipes = Object.values(mealPlan).some(meals => meals.length > 0);
    if (!hasRecipes) {
      toast.error('Add some recipes to your meal plan first');
      return;
    }

    try {
      await api.post('/grocery-lists/from-meal-plan');
      const totalItems = Object.values(mealPlan).reduce((acc, meals) => acc + meals.length, 0);
      trackGroceryListGenerated(totalItems, false);
      toast.success('Grocery list created!');
      navigate('/grocery-lists');
    } catch (error) {
      toast.error('Failed to generate grocery list');
    }
  };

  // Render mobile version
  if (isMobile && isAuthenticated && !isDemoMode) {
    return (
      <MobileMealPlanner
        mealPlan={mealPlan}
        onAddMeal={handleMobileAddMeal}
        onRemoveMeal={handleMobileRemoveMeal}
        onAutoPlan={handleMobileAutoPlan}
        onGenerateGroceryList={handleMobileGenerateList}
        isPro={isPro}
        favorites={favorites}
        pantryAlerts={pantryAlerts}
        isLoading={loading}
      />
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen py-8" data-testid="meal-planner-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Household Collaboration Banner */}
          {isAuthenticated && inHousehold && !isDemoMode && (
            <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-accent/5 mb-6" data-testid="household-sync-banner">
              <CardContent className="py-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <div>
                      <span className="font-medium text-sm">Shared with {householdName}</span>
                      <p className="text-xs text-muted-foreground">
                        {wsConnected 
                          ? 'Real-time sync active — changes appear instantly'
                          : 'Syncing every 30 seconds (connecting...)'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {activeMembersOnline > 1 && (
                      <Badge variant="secondary" className="text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {activeMembersOnline} online
                      </Badge>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      {wsConnected ? (
                        <Wifi className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <WifiOff className="h-3.5 w-3.5 text-yellow-500" />
                      )}
                      {lastSync ? `${lastSync.toLocaleTimeString()}` : 'Connecting...'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Demo Mode Step 2 Banner */}
          {isDemoMode && !isAuthenticated && !showStep3Banner && (
            <Card className="border-accent/50 bg-gradient-to-r from-accent/10 to-primary/5 mb-6" data-testid="demo-step2-planner-banner">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-accent text-accent-foreground">Step 2</Badge>
                      <span className="font-semibold text-foreground">Plan your week in minutes</span>
                    </div>
                    <span className="text-sm text-muted-foreground ml-[52px]">Drag recipes onto your calendar to build your meal plan.</span>
                  </div>
                  <span className="text-xs text-muted-foreground">No planning required — move things around anytime.</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Demo Mode Step 3 Banner */}
          {isDemoMode && !isAuthenticated && showStep3Banner && (
            <Card className="border-primary/50 bg-gradient-to-r from-primary/10 to-accent/5 mb-6" data-testid="demo-step3-banner">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-primary">Step 3</Badge>
                      <span className="font-semibold text-foreground">Generate your grocery list</span>
                    </div>
                    <span className="text-sm text-muted-foreground ml-[52px]">We&apos;ll combine ingredients from your meal plan automatically.</span>
                  </div>
                  <Button size="sm" onClick={generateGroceryList} data-testid="demo-generate-list-cta">
                    Generate List
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-2">
                Your Week
              </h1>
              <p className="text-muted-foreground">
                We&apos;ve planned dinners based on your preferences. Make any changes you want.
              </p>
              <p className="text-xs text-muted-foreground mt-1 italic">Don&apos;t worry — you can change anything.</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {/* Refresh Week Pro Feature CTA */}
              {isPro ? (
                <Dialog open={autoPlanOpen} onOpenChange={setAutoPlanOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="refresh-week-btn">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh my week
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
                        <RefreshCw className="h-5 w-5 text-primary" />
                        Refresh Your Week
                      </DialogTitle>
                      <DialogDescription>
                        Rebuild your week with new meals and optimized ingredient overlap
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                      {/* Number of meals */}
                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <Utensils className="h-4 w-4" />
                          How many meals to plan?
                        </Label>
                        <div className="flex items-center gap-4">
                          <Slider
                            value={[autoPlanPrefs.numMeals]}
                            onValueChange={([val]) => setAutoPlanPrefs(prev => ({ ...prev, numMeals: val }))}
                            min={1}
                            max={7}
                            step={1}
                            className="flex-1"
                          />
                          <Badge variant="secondary" className="w-12 justify-center">
                            {autoPlanPrefs.numMeals}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Dietary preferences */}
                      <div className="space-y-3">
                        <Label>Dietary preferences (optional)</Label>
                        <div className="flex flex-wrap gap-2">
                          {DIETARY_OPTIONS.map(opt => (
                            <Badge
                              key={opt.value}
                              variant={autoPlanPrefs.dietary.includes(opt.value) ? "default" : "outline"}
                              className="cursor-pointer hover:bg-primary/20"
                              onClick={() => toggleDietary(opt.value)}
                            >
                              {opt.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {/* Cuisine preferences */}
                      <div className="space-y-3">
                        <Label>Favorite cuisines (optional)</Label>
                        <div className="flex flex-wrap gap-2">
                          {CUISINE_OPTIONS.map(opt => (
                            <Badge
                              key={opt.value}
                              variant={autoPlanPrefs.cuisines.includes(opt.value) ? "default" : "outline"}
                              className="cursor-pointer hover:bg-primary/20"
                              onClick={() => toggleCuisine(opt.value)}
                            >
                              {opt.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {/* Cook time */}
                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Max cook time
                        </Label>
                        <Select 
                          value={autoPlanPrefs.maxCookTime?.toString() || "any"}
                          onValueChange={(val) => setAutoPlanPrefs(prev => ({ 
                            ...prev, 
                            maxCookTime: val === "any" ? null : parseInt(val)
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Any time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any time</SelectItem>
                            <SelectItem value="30">Under 30 minutes</SelectItem>
                            <SelectItem value="45">Under 45 minutes</SelectItem>
                            <SelectItem value="60">Under 1 hour</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Use favorites */}
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 cursor-pointer">
                          <Heart className="h-4 w-4" />
                          Use my favorites first
                        </Label>
                        <Switch
                          checked={autoPlanPrefs.useFavoritesFirst}
                          onCheckedChange={(checked) => setAutoPlanPrefs(prev => ({ 
                            ...prev, 
                            useFavoritesFirst: checked 
                          }))}
                        />
                      </div>
                      
                      {/* Fill mode */}
                      <div className="space-y-3">
                        <Label>How should we fill the calendar?</Label>
                        <Select 
                          value={autoPlanPrefs.fillMode}
                          onValueChange={(val) => setAutoPlanPrefs(prev => ({ ...prev, fillMode: val }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="empty_only">Fill empty days only</SelectItem>
                            <SelectItem value="replace_all">Replace entire week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAutoPlanOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleAutoPlan} disabled={autoPlanLoading}>
                        {autoPlanLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Planning...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate Plan
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="text-muted-foreground" 
                      onClick={() => navigate('/subscription')}
                      data-testid="refresh-week-btn"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh my week
                      <Lock className="h-3 w-3 ml-2" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium">Rebuild your week with new meals and optimized ingredient overlap</p>
                    <p className="text-xs text-muted-foreground mt-1">Upgrade to Weekly Planner to unlock</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              <Button variant="outline" onClick={clearWeek} data-testid="clear-week-btn">
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Week
              </Button>
              
              {/* Share Button - Available to all users */}
              {isAuthenticated && !isDemoMode && (
                <Button variant="outline" onClick={() => setShareDialogOpen(true)} data-testid="share-meal-plan-btn">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              )}
              
              <div className="flex flex-col items-end gap-1">
                <Button onClick={generateGroceryList} data-testid="generate-list-btn">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Generate List
                </Button>
                <span className="text-xs text-muted-foreground">Combines ingredients from your planned meals</span>
              </div>
            </div>
          </div>
          
          {/* Progress Indicator */}
          {(isDemoMode || isAuthenticated) && (
            <Card className="border-border/50 mb-6" data-testid="progress-card">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Utensils className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      {demoMealsAdded} of {DINNER_GOAL} dinners planned
                    </span>
                    {demoMealsAdded >= DINNER_GOAL && (
                      <Badge className="bg-green-500/20 text-green-700 border-0">
                        <Check className="h-3 w-3 mr-1" />
                        Week planned · Ready to generate your cart
                      </Badge>
                    )}
                  </div>
                  {demoMealsAdded > 0 && demoMealsAdded < DINNER_GOAL && (
                    <span className="text-xs text-muted-foreground">
                      {DINNER_GOAL - demoMealsAdded} more to go
                    </span>
                  )}
                </div>
                <Progress value={(demoMealsAdded / DINNER_GOAL) * 100} className="h-2" />
              </CardContent>
            </Card>
          )}

          {/* Quick Edit Bar - Servings & Dietary Filter */}
          {isAuthenticated && !isDemoMode && demoMealsAdded > 0 && (
            <div className="mb-6">
              <QuickEditBar 
                servings={servings}
                onServingsChange={setServings}
                onDietaryClick={() => setDietaryFilterOpen(true)}
              />
            </div>
          )}

          {/* Week Navigation */}
          <Card className="border-border/50 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setWeekStart(subWeeks(weekStart, 1))}
                  data-testid="prev-week-btn"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                  <span className="font-medium">
                    {format(weekDays[0], 'MMM d')} - {format(weekDays[6], 'MMM d, yyyy')}
                  </span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                  data-testid="next-week-btn"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Calendar Grid */}
          {loading && isAuthenticated ? (
            <div className="grid grid-cols-7 gap-4">
              {[...Array(7)].map((_, i) => (
                <Card key={i} className="border-border/50 min-h-[300px] animate-pulse">
                  <CardHeader className="py-3">
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="h-16 bg-muted rounded" />
                    <div className="h-16 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <>
              {/* Empty State for Demo Mode */}
              {isDemoMode && !isAuthenticated && isCalendarEmpty && (
                <div className="text-center py-12 mb-8 border-2 border-dashed border-border rounded-xl bg-muted/20" data-testid="empty-calendar-state">
                  <CalendarIcon className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-2">Your week starts here</h3>
                  <p className="text-muted-foreground mb-2">Click on any day below to start adding meals.</p>
                  <p className="text-sm text-primary font-medium">Start by adding 2-3 dinners</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {weekDays.map((day, dayIndex) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const meals = mealPlan[dateStr] || [];
                  const isToday = isSameDay(day, new Date());
                  const showTooltip = isDemoMode && showFirstInteractionTooltip && dayIndex === 0 && meals.length === 0;

                  return (
                    <Card 
                      key={dateStr} 
                      className={`border-border/50 min-h-[300px] ${isToday ? 'ring-2 ring-primary' : ''}`}
                      data-testid={`day-${dateStr}`}
                    >
                      <CardHeader className="py-3 px-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase">
                              {format(day, 'EEE')}
                            </p>
                            <p className={`text-lg font-semibold ${isToday ? 'text-primary' : ''}`}>
                              {format(day, 'd')}
                            </p>
                          </div>
                          {isToday && <Badge variant="secondary">Today</Badge>}
                        </div>
                      </CardHeader>
                      <CardContent className="px-3 pb-3 space-y-2">
                        {meals.map((meal, index) => (
                          <Tooltip key={index}>
                            <TooltipTrigger asChild>
                              <div 
                                className="group relative bg-muted/50 rounded-lg p-2 hover:bg-muted transition-colors cursor-move"
                              >
                                <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-50 transition-opacity">
                                  <GripVertical className="h-3 w-3" />
                                </div>
                                {isAuthenticated ? (
                                  <Link to={`/recipes/${meal.recipe_id}`}>
                                    <img 
                                      src={meal.recipe_image}
                                      alt={meal.recipe_title}
                                      className="w-full h-16 object-cover rounded mb-2"
                                    />
                                    <p className="text-xs font-medium line-clamp-2">{meal.recipe_title}</p>
                                  </Link>
                                ) : (
                                  <div onClick={handleDemoSaveAttempt}>
                                    <img 
                                      src={meal.recipe_image}
                                      alt={meal.recipe_title}
                                      className="w-full h-16 object-cover rounded mb-2"
                                    />
                                    <p className="text-xs font-medium line-clamp-2">{meal.recipe_title}</p>
                                  </div>
                                )}
                                {/* Action Buttons */}
                                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {/* Component Swap Button - Authenticated users only */}
                                  {isAuthenticated && !isDemoMode && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 bg-background/80"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openComponentSwap(
                                          meal.recipe_id || meal.id,
                                          meal.recipe_title || meal.title,
                                          dayIndex
                                        );
                                      }}
                                      title="Customize ingredients"
                                      data-testid={`customize-meal-${dateStr}-${index}`}
                                    >
                                      <Utensils className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {/* Swap Button - Authenticated users only */}
                                  {isAuthenticated && !isDemoMode && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 bg-background/80"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSwapMealDayIndex(dayIndex);
                                        setSwapMealTitle(meal.recipe_title);
                                        setSwapMealOpen(true);
                                        haptic('light');
                                      }}
                                      title="Swap meal"
                                      data-testid={`swap-meal-${dateStr}-${index}`}
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {/* Remove Button */}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 bg-background/80"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      removeMealFromDate(dateStr, index);
                                    }}
                                    title="Remove meal"
                                    data-testid={`remove-meal-${dateStr}-${index}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </TooltipTrigger>
                            {isDemoMode && !isAuthenticated && (
                              <TooltipContent>
                                <p>Drag me to another day!</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        ))}

                        <Dialog open={dialogOpen && selectedDate && format(selectedDate, 'yyyy-MM-dd') === dateStr} onOpenChange={(open) => {
                          setDialogOpen(open);
                          if (open) {
                            setSelectedDate(day);
                            setShowFirstInteractionTooltip(false);
                            // Reset add meal mode based on whether there are existing meals
                            const currentMeals = mealPlan[format(day, 'yyyy-MM-dd')] || [];
                            setAddMealMode(currentMeals.length > 0 ? 'choose' : 'browse');
                          } else {
                            // Reset mode when closing
                            setAddMealMode('choose');
                            setSearchQuery('');
                            setSearchResults([]);
                          }
                        }}>
                          <Tooltip open={showTooltip}>
                            <TooltipTrigger asChild>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  className="w-full h-auto min-h-[48px] border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 flex flex-col gap-0.5 py-2"
                                  onClick={() => setSelectedDate(day)}
                                  data-testid={`add-meal-${dateStr}`}
                                >
                                  <div className="flex items-center">
                                    <Plus className="h-4 w-4 mr-1" />
                                    <span>Add Meal</span>
                                  </div>
                                  {meals.length === 0 && (
                                    <span className="text-xs text-muted-foreground font-normal">Click to browse recipes</span>
                                  )}
                                </Button>
                              </DialogTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="bg-primary text-primary-foreground">
                              <p className="font-medium">Click to add a recipe</p>
                              <p className="text-xs opacity-80">Pick from suggestions or search</p>
                            </TooltipContent>
                          </Tooltip>
                          <DialogContent className="sm:max-w-[500px]">
                            {(() => {
                              const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
                              const selectedDateMeals = mealPlan[selectedDateStr] || [];
                              
                              return (
                                <>
                                  <DialogHeader>
                                    <DialogTitle className="font-['Playfair_Display']">
                                      {selectedDateMeals.length > 0 ? 'Change Meal' : 'Add Meal'} for {selectedDate && format(selectedDate, 'EEEE, MMM d')}
                                    </DialogTitle>
                                    <DialogDescription>
                                      {addMealMode === 'choose' ? 'How would you like to update this meal?' : 'Search for a recipe or pick from suggestions'}
                                    </DialogDescription>
                                  </DialogHeader>

                                  {/* Initial Choice Menu - shown when there's already a meal */}
                                  {addMealMode === 'choose' && selectedDateMeals.length > 0 && (
                                    <div className="space-y-3 mt-4">
                                      <Button 
                                        variant="outline" 
                                        className="w-full justify-start h-auto py-3 px-4 border-primary/50"
                                        onClick={() => {
                                          setAddMealMode('swap');
                                          // Auto-search for similar meals
                                          if (selectedDateMeals[0]?.recipe_title) {
                                            const keywords = selectedDateMeals[0].recipe_title.split(' ').slice(0, 2).join(' ');
                                            setSearchQuery(keywords);
                                          }
                                        }}
                                        data-testid="swap-similar-btn"
                                      >
                                        <RefreshCw className="h-5 w-5 mr-3 text-primary" />
                                        <div className="text-left">
                                          <p className="font-medium">Swap with a similar meal</p>
                                          <p className="text-xs text-muted-foreground">Recommended — keeps your plan balanced</p>
                                        </div>
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        className="w-full justify-start h-auto py-3 px-4"
                                        onClick={() => setAddMealMode('browse')}
                                        data-testid="browse-all-btn"
                                      >
                                        <Search className="h-5 w-5 mr-3" />
                                        <div className="text-left">
                                          <p className="font-medium">Browse all meals</p>
                                          <p className="text-xs text-muted-foreground">Search our full recipe catalog</p>
                                        </div>
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        className="w-full justify-start h-auto py-3 px-4"
                                        onClick={() => setAddMealMode('favorites')}
                                        data-testid="add-favorites-btn"
                                      >
                                        <Heart className="h-5 w-5 mr-3" />
                                        <div className="text-left">
                                          <p className="font-medium">Add from favorites</p>
                                          <p className="text-xs text-muted-foreground">Pick from your saved recipes</p>
                                        </div>
                                      </Button>
                                    </div>
                                  )}

                                  {/* Search/Browse Mode */}
                                  {(addMealMode !== 'choose' || selectedDateMeals.length === 0) && addMealMode !== 'favorites' && (
                                    <div className="space-y-4 mt-4">
                                      {/* Back button when in sub-mode */}
                                      {selectedDateMeals.length > 0 && addMealMode !== 'choose' && (
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          onClick={() => setAddMealMode('choose')}
                                          className="mb-2"
                                        >
                                          <ChevronLeft className="h-4 w-4 mr-1" />
                                          Back to options
                                        </Button>
                                      )}
                                      
                                      {/* Search */}
                                      <div className="flex gap-2">
                                        <Input
                                          placeholder="Search recipes..."
                                          value={searchQuery}
                                          onChange={(e) => setSearchQuery(e.target.value)}
                                          onKeyDown={(e) => e.key === 'Enter' && searchRecipes()}
                                          data-testid="meal-search-input"
                                        />
                                        <Button onClick={searchRecipes} disabled={searchLoading}>
                                          {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                        </Button>
                                      </div>

                                      {/* Search Results */}
                                      {searchResults.length > 0 && (
                                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                          <p className="text-sm text-muted-foreground">Search Results</p>
                                          {searchResults.map((recipe) => (
                                            <Button
                                              key={recipe.id}
                                              variant="ghost"
                                              className="w-full justify-start h-auto p-2"
                                              onClick={() => addMealToDate(recipe)}
                                              data-testid={`search-result-${recipe.id}`}
                                            >
                                              <img 
                                                src={recipe.image}
                                                alt={recipe.title}
                                                className="w-12 h-12 object-cover rounded mr-3"
                                              />
                                              <span className="text-sm text-left line-clamp-2">{recipe.title}</span>
                                            </Button>
                                          ))}
                                        </div>
                                      )}

                                      {/* Demo Mode Suggestions */}
                                      {isDemoMode && !isAuthenticated && searchResults.length === 0 && (
                                        <div className="space-y-2">
                                          <p className="text-sm text-muted-foreground">Suggested Recipes</p>
                                          <div className="max-h-[200px] overflow-y-auto space-y-2">
                                            {mockDemoRecipes.map((recipe) => (
                                              <Button
                                                key={recipe.id}
                                                variant="ghost"
                                                className="w-full justify-start h-auto p-2"
                                                onClick={() => addMealToDate(recipe)}
                                                data-testid={`demo-recipe-${recipe.id}`}
                                              >
                                                <img 
                                                  src={recipe.image}
                                                  alt={recipe.title}
                                                  className="w-12 h-12 object-cover rounded mr-3"
                                                />
                                                <div className="text-left">
                                                  <span className="text-sm line-clamp-1">{recipe.title}</span>
                                                  <p className="text-xs text-muted-foreground">{recipe.readyInMinutes} min</p>
                                                </div>
                                              </Button>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Favorites Mode */}
                                  {addMealMode === 'favorites' && (
                                    <div className="space-y-4 mt-4">
                                      {/* Back button */}
                                      {selectedDateMeals.length > 0 && (
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          onClick={() => setAddMealMode('choose')}
                                          className="mb-2"
                                        >
                                          <ChevronLeft className="h-4 w-4 mr-1" />
                                          Back to options
                                        </Button>
                                      )}
                                      
                                      {favorites.length > 0 ? (
                                        <div className="space-y-2">
                                          <p className="text-sm text-muted-foreground">Your Favorites</p>
                                          <div className="max-h-[250px] overflow-y-auto space-y-2">
                                            {favorites.map((fav) => (
                                              <Button
                                                key={fav._id || fav.id}
                                                variant="ghost"
                                                className="w-full justify-start h-auto p-2"
                                                onClick={() => addMealToDate(fav)}
                                                data-testid={`favorite-${fav._id || fav.id}`}
                                              >
                                                <img 
                                                  src={fav.recipe_image || fav.image}
                                                  alt={fav.recipe_title || fav.title}
                                                  className="w-12 h-12 object-cover rounded mr-3"
                                                />
                                                <span className="text-sm text-left line-clamp-2">{fav.recipe_title || fav.title}</span>
                                              </Button>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                          <Heart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                          <p>No favorites yet</p>
                                          <p className="text-sm">Save recipes from search to see them here</p>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Empty state for authenticated users with no favorites and no search results */}
                                  {isAuthenticated && addMealMode === 'browse' && favorites.length === 0 && searchResults.length === 0 && !searchLoading && (
                                    <div className="text-center py-8 text-muted-foreground">
                                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                      <p className="text-sm">Search for recipes to add to your plan</p>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Partial Planning Encouragement */}
              {isDemoMode && !isAuthenticated && demoMealsAdded > 0 && demoMealsAdded < 3 && (
                <div className="mt-6 text-center">
                  <p className="text-muted-foreground">
                    <span className="text-primary font-medium">Nice start!</span> Add a few more meals to complete your week.
                  </p>
                </div>
              )}
            </>
          )}

          {saving && (
            <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </div>
          )}

          {/* Demo Signup Modal */}
          <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-xl">
                  Save your meal plan
                </DialogTitle>
                <DialogDescription>
                  Create a free account to keep your weekly plan and continue.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 mt-4">
                <Link to="/auth" className="w-full">
                  <Button className="w-full" size="lg" data-testid="demo-signup-modal-cta">
                    Create free account
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowSignupModal(false)}
                  data-testid="demo-continue-browsing"
                >
                  Continue browsing
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Share Meal Plan Dialog */}
          <ShareMealPlanDialog 
            open={shareDialogOpen} 
            onClose={() => setShareDialogOpen(false)} 
          />
          
          {/* Light Edit - Swap Meal Sheet */}
          <SwapMealSheet
            open={swapMealOpen}
            onOpenChange={setSwapMealOpen}
            dayIndex={swapMealDayIndex}
            currentMeal={swapMealTitle}
            onSwap={loadMealPlan}
          />
          
          {/* Light Edit - Dietary Filter Sheet */}
          <DietaryFilterSheet
            open={dietaryFilterOpen}
            onOpenChange={setDietaryFilterOpen}
            onApply={loadMealPlan}
          />
          
          {/* Component Swap Sheet */}
          <ComponentSwapSheet
            open={componentSwapOpen}
            onOpenChange={setComponentSwapOpen}
            mealId={componentSwapMealId}
            mealTitle={componentSwapMealTitle}
            dayIndex={componentSwapDayIndex}
            onSwap={loadMealPlan}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default MealPlanner;
